/**
 * Typed fetch wrapper around the JSON v1 API.
 *
 * - All requests include credentials (cookie-bound JWT session).
 * - 401/403 are surfaced as ApiCallError so callers can handle auth flows.
 * - The notes PUT endpoint is form-encoded (matches `Content-Type: form`)
 *   while everything else is JSON.
 */

import type { ApiError, AnalyzeRequest, JobResponse, LoginResponse, NoteResponse, SaveResponse, SessionResponse, FileListResponse, NoteHistoryResponse, AnalysisResponse, AnalysisHistoryResponse, FileRawResponse, FileMetaResponse, UserOut, AdminPermissionMatrix, SearchResponse, BadgeCount, MarkdownDocument, WorkshopProject, WorkshopNode, WorkshopFileRawResponse } from '@/types/api';

type GrantKind = 'md' | 'pdf' | 'workshop';

/**
 * Encode a filename for use in a URL path. Markdown filenames may contain
 * one `/` (a subfolder prefix, e.g. `"cbeh/notes.md"`) which must survive
 * as a literal path separator — a single `encodeURIComponent` call would
 * turn it into `%2F`, which the backend's `{filename:path}` routes don't
 * accept (percent-decoding happens before route matching). Encoding each
 * segment individually and rejoining with `/` keeps the separator literal
 * while still escaping anything else that needs it.
 */
export function encodeFilename(filename: string): string {
  return filename.split('/').map(encodeURIComponent).join('/');
}

export class ApiCallError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(`${status} ${detail}`);
    this.name = 'ApiCallError';
    this.status = status;
    this.detail = detail;
  }
}

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Force a form-encoded body (used by /api/v1/notes PUT). */
  formBody?: Record<string, string>;
  /** Query-string params appended to the URL. */
  params?: Record<string, string | number | undefined | null>;
}

async function request<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const { body, formBody, params, headers, ...rest } = opts;

  let url = path;
  if (params) {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      usp.set(k, String(v));
    }
    const qs = usp.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json', ...(headers as Record<string, string>) },
    ...rest,
  };

  if (body !== undefined) {
    init.headers = { 'Content-Type': 'application/json', ...(init.headers as Record<string, string>) };
    init.body = JSON.stringify(body);
  } else if (formBody) {
    init.headers = { 'Content-Type': 'application/x-www-form-urlencoded', ...(init.headers as Record<string, string>) };
    init.body = new URLSearchParams(formBody).toString();
  }

  const res = await fetch(url, init);
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const detail =
      (parsed && typeof parsed === 'object' && 'detail' in parsed && typeof (parsed as ApiError).detail === 'string')
        ? (parsed as ApiError).detail
        : res.statusText || `HTTP ${res.status}`;
    throw new ApiCallError(res.status, detail);
  }

  return parsed as T;
}

export const api = {
  // ── Auth ────────────────────────────────────────────────────────────
  session: () => request<SessionResponse>('/api/v1/auth/session'),
  login: (username: string, password: string) =>
    request<LoginResponse>('/api/v1/auth/login', {
      method: 'POST',
      formBody: { username, password },
    }),
  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST' }),

  // ── Files ───────────────────────────────────────────────────────────
  files: () => request<FileListResponse>('/api/v1/files'),
  fileRaw: (filename: string) =>
    request<FileRawResponse>(`/api/v1/files/${encodeFilename(filename)}/raw`),
  fileMeta: (filename: string) =>
    request<FileMetaResponse>(`/api/v1/files/${encodeFilename(filename)}/meta`),

  // ── Notes ───────────────────────────────────────────────────────────
  getNote: (filename: string) => request<NoteResponse>(`/api/v1/notes/${encodeFilename(filename)}`),
  saveNote: (filename: string, content: string) =>
    request<SaveResponse>(`/api/v1/notes/${encodeFilename(filename)}`, {
      method: 'PUT',
      formBody: { content },
    }),
  noteHistory: (filename: string) =>
    request<NoteHistoryResponse>(`/api/v1/notes/${encodeFilename(filename)}/history`),

  // ── Analyses ────────────────────────────────────────────────────────
  getAnalysis: (filename: string, question = '') =>
    request<AnalysisResponse>(`/api/v1/analyses/${encodeFilename(filename)}`, {
      params: { question },
    }),
  analysisHistory: (filename: string, question = '') =>
    request<AnalysisHistoryResponse>(`/api/v1/analyses/${encodeFilename(filename)}/history`, {
      params: { question },
    }),

  // ── Analyze (submit + long-poll) ────────────────────────────────────
  submitAnalysis: (req: AnalyzeRequest) =>
    request<JobResponse>('/api/v1/analyze', { method: 'POST', body: req }),
  jobStatus: (jobId: string, since = 0, timeout = 25) =>
    request<import('@/types/api').JobStatus>(`/api/v1/job/${jobId}`, {
      params: { since, timeout },
    }),
  cancelJob: (jobId: string) =>
    request<void>(`/api/v1/job/${jobId}`, { method: 'DELETE' }),

  // ── Admin (require admin session) ────────────────────────────────────
  adminListUsers: () => request<UserOut[]>('/api/v1/admin/users'),
  adminCreateUser: (username: string, password: string, isAdmin: boolean) =>
    request<UserOut>('/api/v1/admin/users', {
      method: 'POST',
      body: { username, password, is_admin: isAdmin },
    }),
  adminDeleteUser: (userId: string) =>
    request<void>(`/api/v1/admin/users/${userId}`, { method: 'DELETE' }),
  adminChangeUserPassword: (userId: string, newPassword: string) =>
    request<void>(`/api/v1/admin/users/${userId}/password`, {
      method: 'PUT',
      body: { password: newPassword },
    }),
  adminPermissionsMatrix: () =>
    request<AdminPermissionMatrix>('/api/v1/admin/permissions'),
  adminGrant: (kind: GrantKind, filename: string, userId: string) =>
    request<void>(
      `/api/v1/admin/files/${kind}/${encodeFilename(filename)}/grant`,
      { method: 'POST', body: { user_id: userId, filename } },
    ),
  adminRevoke: (kind: GrantKind, filename: string, userId: string) =>
    request<void>(
      `/api/v1/admin/files/${kind}/${encodeFilename(filename)}/grant`,
      { method: 'DELETE', params: { user_id: userId } },
    ),
  adminBulkGrant: (
    kind: GrantKind,
    userId: string,
    filenames: string[],
  ) =>
    request<void>(`/api/v1/admin/files/${kind}/bulk-grant`, {
      method: 'POST',
      body: { user_id: userId, filenames },
    }),

  // ── Search ──────────────────────────────────────────────────────────
  search: (q: string, badge?: string | null) =>
    request<SearchResponse>('/api/v1/search', { params: { q, badge } }),
  badgeCounts: () => request<BadgeCount[]>('/api/v1/badges'),

  // ── Workshops ───────────────────────────────────────────────────────
  workshops: () => request<WorkshopProject[]>('/api/v1/workshops'),
  workshopFiles: (projectId: string) =>
    request<WorkshopNode[]>(
      `/api/v1/workshops/${encodeURIComponent(projectId)}/files`,
    ),
  workshopFileRaw: (projectId: string, filePath: string) =>
    request<WorkshopFileRawResponse>(
      `/api/v1/workshops/${encodeURIComponent(projectId)}/files/${encodeFilename(filePath)}/raw`,
    ),

  // ── Markdown document CRUD (admin-only) ────────────────────────────────
  adminCreateMarkdown: (filename: string, content: string, badges: string[] = []) =>
    request<MarkdownDocument>('/api/v1/files', {
      method: 'POST',
      body: { filename, content, badges },
    }),
  adminUpdateMarkdown: (filename: string, content: string, badges: string[]) =>
    request<MarkdownDocument>(`/api/v1/files/${encodeFilename(filename)}`, {
      method: 'PUT',
      body: { content, badges },
    }),
  adminDeleteMarkdown: (filename: string) =>
    request<void>(`/api/v1/files/${encodeFilename(filename)}`, { method: 'DELETE' }),
};
